import { format, Locale } from 'date-fns';
import { de, enGB, fr } from 'date-fns/locale';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useCustomTranslations } from '../../../../../../hooks/useCustomTranslations';
import { AgendaComponentInterface, AgendaEntry, AgendaGroupBy, SpeakerEntry, UiComponentElementInterface } from '../../../../../../interfaces/pagebuilder';
import { setSpeakerModal } from '../../../../../../reduxToolkit/slices/commonSlice';
import EmptyComponent from '../../../../pageBuilder/EmptyComponent';
import { TitleAndDescription } from '../../../../pageBuilder/TitleAndDescription';
import Entry from './Entry';
import EntryGroup from './EntryGroup';

const AgendaUIComponent = ({ uiComponent }: UiComponentElementInterface<AgendaComponentInterface>) => {
  const { i18n, t } = useTranslation();
  const { ct } = useCustomTranslations();
  const dispatch = useDispatch();

  const [sortedEntries, setSortedEntries] = useState<AgendaEntry[]>([]);

  useEffect(() => {
    if (!uiComponent?.entryList) return;
    setSortedEntries([...uiComponent?.entryList].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()) || []);
  }, [uiComponent?.entryList]);

  if (!uiComponent) return null;

  const groupByProp = (groupProp: AgendaGroupBy) =>
    uiComponent?.entryList?.reduce((p, entry) => {
      const prop = calculateProp(entry.timestamp, groupProp);
      if (!p.hasOwnProperty(prop)) p[prop] = [];
      p[prop].push(entry);
      return p;
    }, {} as Record<string, AgendaEntry[]>);

  function calculateProp(timestamp: string, groupProp: AgendaGroupBy) {
    const date = new Date(timestamp);
    return getFormatProp(date, groupProp);
  }

  function _getFormatLocale(): Locale {
    switch (i18n.language) {
      case 'de':
        return de;
      case 'fr':
        return fr;
      default:
        return enGB;
    }
  }

  function getFormatProp(date: Date, groupProp: AgendaGroupBy) {
    const formatString = getFormatString(groupProp);

    return format(date, formatString, { locale: _getFormatLocale() });
  }

  function getFormatString(groupProp: AgendaGroupBy) {
    const groupByFormatMapper: Record<AgendaGroupBy, string> = {
      none: '',
      day: 'iiii',
      hour: 'HH',
    };
    return groupByFormatMapper[groupProp];
  }

  function handleOpenSpeaker(speaker: SpeakerEntry) {
    if (!speaker) return;
    // dispatch speaker
    dispatch(
      setSpeakerModal({
        speaker,
      })
    );
  }

  function sortAgendaGroups(a: [string, AgendaEntry[]], b: [string, AgendaEntry[]]) {
    try {
      if (uiComponent?.groupBy === AgendaGroupBy.hour) return parseInt(a[0], 10) - parseInt(b[0], 10);
      else return new Date(a[1][0]?.timestamp).getTime() - new Date(b[1][0]?.timestamp).getTime();
    } catch (err) {
      console.error(err);
      return 0;
    }
  }

  return (
    <>
      <TitleAndDescription component={uiComponent!} />
      <div
        className={`w-full`}
        style={{
          color: uiComponent?.styling?.textColor || '#000',
        }}>
        <div className="flex flex-col space-y-2 @md:space-y-0 @md:flex-row @md:space-x-2 items-center">
          {(!!!uiComponent?.entryList?.length || uiComponent?.entryList?.length === 0) && <EmptyComponent componentName={t('pagebuilder:agenda')} />}
        </div>
        {uiComponent?.entryList && uiComponent?.entryList?.length !== 0 && (
          <div className="agenda-wrapper">
            <div className="flex flex-col items-start justify-start @md:space-y-0 space-y-8">
              {uiComponent.groupBy === AgendaGroupBy.none &&
                sortedEntries.map((e, index) => (
                  <Entry
                    withHoverAnimation={uiComponent.enableHoverEffect}
                    key={index + e.title}
                    entry={e}
                    openSpeaker={handleOpenSpeaker}
                    uiComponent={uiComponent}
                  />
                ))}

              {uiComponent.groupBy !== AgendaGroupBy.none &&
                Object.entries(groupByProp(uiComponent.groupBy))
                  .sort(sortAgendaGroups)
                  .map(([prop, entries]) => <EntryGroup key={prop} entries={entries} label={prop} openSpeaker={handleOpenSpeaker} uiComponent={uiComponent} />)}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AgendaUIComponent;
